import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

import { BaseControl } from '../../../shared/classes/form-base-controls';
import { TypeChecker } from '../../../shared/classes/type-checker';

import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { FormCreatorService } from '../../../services';
import { AccessRole, FileUploadControl } from '../../../shared';

@Component({
  selector: 'app-file-upload-control',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    FileUploadModule, HttpClientModule, ButtonModule
  ],
  template: `
    <div [formGroup]="formGroup" *ngIf="typeChecker.isFileUpload(control)" class="w-full">
      <div *ngIf="!control.downloadFileMode ; else downloadFileMode" class="w-full flex justify-content-start flex-column">
        @if(control.multipleFiles){
          <p-fileUpload #fileUploadMultiple
							mode="advanced"
							[accept]="control.accept.join(',')"
							[maxFileSize]="uploadFileSize"
							[chooseLabel]="control.chooseLabel"
							chooseIcon="pi pi-upload"
							[customUpload]="true"
							[auto]="true"
							[multiple]="true"
              [disabled]="isDesigner"
							(uploadHandler)="uploadHandler($event, fileUploadMultiple, control)">
          </p-fileUpload>
        }@else{
          <div class="flex flex-column justify-content-end align-items-start">
            <div class="flex">
              <p-fileUpload #fileUpload mode="basic"
              [accept]="control.accept.join(',')"
              [chooseLabel]="control.chooseLabel"
              [customUpload]="true"
              [auto]="true"
              class=""
              (uploadHandler)="uploadHandler($event, fileUpload, control)"
              [disabled]="isDesigner"></p-fileUpload>
              <button pButton icon="pi pi-times" class="p-button-cancel-fileupload border-noround-left cancel" (click)="removeFile()" [disabled]="!files.length"></button>
            </div>
            <ng-container *ngIf="invalidSize">
                <small class="p-error block"> <strong>{{files[0].name}}</strong>: Tamaño de archivo inválido, el tamaño máximo de carga es 1.000 MB. </small>
            </ng-container>
          </div>
          <div *ngIf="files.length > 0 && !invalidSize">
              <p>Archivo "{{files[0].name}}" fue subido.</p>
          </div>
        }
      </div>
      <ng-template #downloadFileMode>
        <div class="flex flex-column gap-2">
          @for(file of formControl.value; track $index){
            <div class="flex gap-2">
              <button pButton icon="pi pi-desktop" class="btn-file" 
                [label]="file.name"                
                (click)="downloadFile(false, file.name, file.file)">
              </button>
            </div>
          }
        </div>
      </ng-template>
    </div>
  `,
  styles: `

    .btn-file{
      width:auto;
    }
    :host {
      display: contents;
    }
  `
})
export class FileUploadControlComponent implements OnInit {
  @Input() control !: BaseControl<any>;
  @Input() formGroup !: FormGroup;
  files: File[] = [];
  invalidSize = false;
  typeChecker = TypeChecker;
  permiso = AccessRole;
  @Input() isDesigner !: boolean;

  readonly uploadFileSize: number = 1024 * 1024

  @ViewChild('fileUpload') fileUpload!: FileUpload;
  @ViewChild('fileUploadMultiple') fileUploadMultiple!: FileUpload;

  constructor(private readonly _fcs: FormCreatorService) {
  }

  ngOnInit(): void {
    this.control.resetEvent.subscribe(() => {
      this.removeFile();
    });
  }

  private _formControl !: AbstractControl;
  get formControl() {
    if (this._formControl)
      return this._formControl;
    this._formControl = this.formGroup.get(this.control.name) as AbstractControl;
    return this._formControl;
  }

  removeFile() {
    this.files = [];
    this.invalidSize = false;
    this.formControl?.setValue(null);
    this.fileUpload?.clear();
    this.fileUploadMultiple?.clear();
  }

  uploadHandler(event: FileUploadHandlerEvent, fileUpload: FileUpload, uploadControl: FileUploadControl) {
    // Handle all uploads as multiple files
    const uploadedFiles = event.files;
    this.invalidSize = false;

    // Check if any file exceeds the size limit
    const oversizedFiles = uploadedFiles.filter(file => file.size > this.uploadFileSize);
    if (oversizedFiles.length > 0) {
      this.invalidSize = true;
      fileUpload.clear();
      return;
    }

    this.files = [...uploadedFiles];

    // Process all files and convert them to base64
    const base64Promises = uploadedFiles.map(file => {
      return new Promise<any>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
          const base64File = e.target?.result as string;
          resolve({ name: file.name, file: base64File });
        };
        reader.readAsDataURL(file);
      });
    });

    // When all files are processed, set the form control value
    Promise.all(base64Promises).then(base64Files => {
      this.formControl?.setValue(base64Files);
      if (!uploadControl.multipleFiles) {
        fileUpload.clear();
      }
    });
  }

  downloadFile(saveFile: boolean, fileName: string, fileId: string) {
    if (this.control && TypeChecker.isFileUpload(this.control) && this.control.downloadFileMode) {
      this._fcs.emitDownloadFileEvent(fileId, fileName, saveFile);
    }
  }
}
